// redux/slices/chatSessionsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getChatSessions, renameChatSession } from '../../api/api';

// 🔄 Fetch all chat sessions
export const fetchChatSessions = createAsyncThunk(
  'chatSessions/fetchChatSessions',
  async (_, { getState, rejectWithValue }) => {
    try {
      const uid = getState().auth.user?.id;
      if (!uid) throw new Error('User ID not found');

      const res = await getChatSessions({ uid, limit: 100, offset: 0 });
      return res.sessions;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Error fetching sessions');
    }
  }
);

// 🔄 Rename API request
export const renameChatSessionThunk = createAsyncThunk(
  'chatSessions/renameChatSession',
  async ({ chatId, newTitle }, { rejectWithValue }) => {
    try {
      await renameChatSession(chatId, newTitle);
      return { chatId, newTitle };
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to rename chat');
    }
  }
);

const chatSessionsSlice = createSlice({
  name: 'chatSessions',
  initialState: {
    sessions: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearChatSessions: (state) => {
      state.sessions = [];
    },

    // ⭐ Instant local UI update (Header + Sidebar update instantly)
    updateChatTitleLocally: (state, action) => {
      const { chatId, newTitle } = action.payload;

      const chat = state.sessions.find((c) => c.chat_session_id === chatId);

      if (chat) chat.title = newTitle;
    },
  },

  extraReducers: (builder) => {
    builder
      // Fetch sessions
      .addCase(fetchChatSessions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.sessions = action.payload;
      })
      .addCase(fetchChatSessions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch chat sessions';
      })

      // Rename (API-confirmed)
      .addCase(renameChatSessionThunk.fulfilled, (state, action) => {
        const { chatId, newTitle } = action.payload;

        const chat = state.sessions.find((c) => c.chat_session_id === chatId);

        if (chat) chat.title = newTitle; // ensure sync with backend
      });
  },
});

export const { clearChatSessions, updateChatTitleLocally } =
  chatSessionsSlice.actions;

export default chatSessionsSlice.reducer;
